/**
 * phoneUtils — utilidades cliente para normalizar numeros de telefono
 * y dejarlos en el formato que espera cada proveedor antes de enviar al backend.
 *
 * Nota: el backend tambien normaliza con `normalize_phone_mx` antes de llegar a
 * Twilio. Esta capa cliente existe para evitar que dato mal formateado se
 * persista en Mongo y para unificar la UX en los 24 puntos de entrada.
 */

// Codigos soportados por parseStoredPhone (orden evaluacion fijo segun spec)
// Nota: la UI acepta mas paises via PhoneInput (ver COUNTRIES). Si el codigo
// no esta aqui, parseStoredPhone cae al fallback MX.
const SUPPORTED_PARSE_CODES = [
  { code: '1', length: 11 },   // US/CA
  { code: '52', length: 12 },  // MX
  { code: '34', length: 11 },  // ES
  { code: '57', length: 12 },  // CO
  { code: '54', length: 12 },  // AR
  { code: '56', length: 11 },  // CL
];

function onlyDigits(s) {
  return String(s ?? '').replace(/\D+/g, '');
}

/**
 * normalizePhone — construye el formato destino segun proveedor.
 *
 * @param {string} raw          lo que escribio el usuario (puede traer + y separadores)
 * @param {string} countryCode  codigo numerico sin "+", ej. "52"
 * @param {string} provider     "twilio" | "gupshup" | "meta_cloud" | "evolution" | "default"
 * @returns {string} formato destino (vacio si no hay digitos locales)
 */
export function normalizePhone(raw, countryCode = '52', provider = 'default') {
  const rawDigits = onlyDigits(raw);
  const cc = onlyDigits(countryCode) || '52';

  if (!rawDigits) return '';

  // Si el raw ya incluye el countryCode al inicio, lo eliminamos para no duplicar.
  // Ejemplos: raw="+525520952500" cc="52" → local "5520952500"
  //           raw="525520952500"  cc="52" → local "5520952500"
  //           raw="5520952500"    cc="52" → local "5520952500"
  let local = rawDigits;
  if (local.startsWith(cc) && local.length > cc.length) {
    local = local.slice(cc.length);
  }

  // Caso especial MX: si queda "1" + 10 digitos (prefijo movil heredado),
  // no lo removemos aqui — el backend de Twilio ya lo maneja. Dejamos tal cual.
  // Si local empieza con 0 (marcacion local nacional) lo limpiamos.
  if (local.startsWith('0')) local = local.replace(/^0+/, '');

  if (!local) return '';

  // Caso especial: Gupshup WhatsApp para México exige el prefijo móvil "1"
  // después del código de país "52" (formato WhatsApp: 521 + 10 dígitos).
  // Otros providers (Twilio/Meta Cloud) NO requieren este "1" para MX;
  // Twilio lo resuelve internamente, por eso esta lógica vive solo aquí.
  // Ejemplos esperados (provider=gupshup, cc=52):
  //   user: "5623202226"     (10) → local "5623202226"   → "5215623202226"
  //   user: "525623202226"   (12) → local "5623202226"   → "5215623202226"
  //   user: "5215623202226"  (13) → local "15623202226"  → "5215623202226" (idempotente)
  //   user: "+5215623202226"      → local "15623202226"  → "5215623202226"
  if ((provider || '').toLowerCase() === 'gupshup' && cc === '52') {
    if (local.length === 10) {
      // Número MX sin prefijo móvil → agregar "1".
      local = `1${local}`;
    }
    // Si ya inicia con "1" y tiene 11 dígitos, se deja como está (idempotente).
  }

  const digitsOnly = `${cc}${local}`;
  const e164 = `+${digitsOnly}`;

  switch ((provider || 'default').toLowerCase()) {
    case 'gupshup':
      return digitsOnly;
    case 'twilio':
    case 'meta_cloud':
    case 'evolution':
    case 'default':
    default:
      return e164;
  }
}

/**
 * parseStoredPhone — descompone un numero persistido en { raw, countryCode }
 * para cargar un valor existente en PhoneInput.
 *
 * @param {string} stored string guardado (puede traer +, espacios, separadores)
 * @returns {{ raw: string, countryCode: string }}
 */
export function parseStoredPhone(stored) {
  const digits = onlyDigits(stored);
  if (!digits) return { raw: '', countryCode: '52' };

  // Caso especial MX móvil Gupshup: "521" + 10 dígitos (13 total).
  // Extraemos solo los 10 últimos dígitos para que el input muestre el número
  // "humano" sin el prefijo móvil "1", evitando duplicarlo al re-normalizar.
  if (digits.length === 13 && digits.startsWith('521')) {
    return { raw: digits.slice(3), countryCode: '52' };
  }

  for (const { code, length } of SUPPORTED_PARSE_CODES) {
    if (digits.length === length && digits.startsWith(code)) {
      return { raw: digits.slice(code.length), countryCode: code };
    }
  }
  return { raw: digits, countryCode: '52' };
}

/**
 * formatLegacyE164 — helper usado por el modo legacy de PhoneInput para
 * construir el string unico que onChange entrega al caller: E.164 sin espacio.
 *
 * @param {string} countryCode "52"
 * @param {string} raw         digitos locales sin codigo
 * @returns {string} "+525520952500" o "" si raw vacio
 */
export function formatLegacyE164(countryCode, raw) {
  const cc = onlyDigits(countryCode) || '52';
  const local = onlyDigits(raw);
  if (!local) return '';
  return `+${cc}${local}`;
}

export default { normalizePhone, parseStoredPhone, formatLegacyE164 };
